/**
 * Build span / column model for products.
 * Columns:
 *  FL FR (front-left/right)
 *  RL RR (rear-left/right)
 */
import { getProductTypeFromMetadata } from '../index'; // adjust if different path

export function buildSpanModel(products, productGroupsByType, metadata) {
  const getType = (product) => getProductTypeFromMetadata(metadata?.[product.choiceId]?.metadata);
  const getHeight = (product) => {
    const gs = metadata?.[product.choiceId]?.metadata?.GridSpan;
    return gs ? parseInt(gs.split(',')[2], 10) : -100000;
  };

  const productSectionMap = {};
  productGroupsByType.forEach((group) => {
    if (group.type === 'multi-stack') {
      ['front', 'rear'].forEach((section) => {
        const sectionData = group[section];
        (sectionData?.stackGroups || []).forEach((stackGroup) => {
          (stackGroup.products || []).forEach((product) => {
            if (getType(product) !== 'full') productSectionMap[product.stackItemId] = section;
          });
        });
      });
    }
  });

  const colHeights = { FL: 0, FR: 0, RL: 0, RR: 0 };
  const colEntries = { FL: [], FR: [], RL: [], RR: [] };
  const productSpanInfo = {};

  const getColumnsForProduct = (product) => {
    const type = getType(product);
    if (type === 'full') return ['FL', 'FR', 'RL', 'RR'];
    const section = productSectionMap[product.stackItemId];
    if (!section) return [];
    const isLeft = product.Origin && product.Origin[1] < 2;
    if (type === 'narrow') {
      if (section === 'front') return isLeft ? ['FL'] : ['FR'];
      return isLeft ? ['RL'] : ['RR'];
    }
    if (type === 'wide') {
      if (section === 'front') return ['FL', 'FR'];
      return ['RL', 'RR'];
    }
    return [];
  };

  // Placement simulation (in insertion order)
  products.forEach((product) => {
    const columns = getColumnsForProduct(product);
    if (!columns.length) return;
    const height = getHeight(product);
    const base = Math.max(...columns.map((col) => colHeights[col]));
    const top = base + height;

    productSpanInfo[product.stackItemId] = {
      product,
      type: getType(product),
      columns,
      bottom: base,
      top
    };

    columns.forEach((col) => {
      colEntries[col].push({ product, bottom: base, top });
      colHeights[col] = top;
    });
  });

  // Immediate supports
  const supportSetMap = {};
  Object.values(productSpanInfo).forEach((info) => {
    const supports = new Set();
    info.columns.forEach((col) => {
      const entry = colEntries[col].find((colEntry) => colEntry.top === info.bottom);
      if (entry) supports.add(entry.product.stackItemId);
    });
    supportSetMap[info.product.stackItemId] = supports;
  });

  // Dependents (inverse)
  const dependentsMap = {};
  Object.entries(supportSetMap).forEach(([pid, supports]) => {
    supports.forEach((supportId) => {
      if (!dependentsMap[supportId]) dependentsMap[supportId] = new Set();
      dependentsMap[supportId].add(pid);
    });
  });

  // hasAbove calculation (column overlap & vertical)
  const hasAbove = {};
  Object.values(productSpanInfo).forEach((info) => { hasAbove[info.product.stackItemId] = false; });
  const spanArray = Object.values(productSpanInfo);
  for (let idxA = 0; idxA < spanArray.length; idxA += 1) {
    for (let idxB = 0; idxB < spanArray.length; idxB += 1) {
      if (idxA !== idxB) {
        const spanA = spanArray[idxA];
        const spanB = spanArray[idxB];
        if (spanA.top <= spanB.bottom) {
          const hasSharedColumn = spanA.columns.some((colA) => spanB.columns.includes(colA));
          if (hasSharedColumn && spanB.bottom >= spanA.top) {
            hasAbove[spanA.product.stackItemId] = true;
          }
        }
      }
    }
  }

  return {
    productSpanInfo,
    supportSetMap,
    dependentsMap,
    hasAbove
  };
}
